import React from 'react'; //eslint-disable-line
import useAppContext from '../../../context/useAppContext';
import PropTypes from 'prop-types';

import { useParams, useHistory, Link } from 'react-router-dom';

import i18n from 'i18next';

import {
    Status,
    Icon,
    InfoText,
    WeezHeader,
    Numbers,
    SplitContainer,
    AnchorNav,
    Card,
    HeaderCard,
    FrameCard,
    ButtonDropdown,
    Button,
    Tooltip,
    CopyToClipboardMinimal
} from '@weezevent/nacre';
import { getMenhirUrl } from '../../../utils/config';
import LabelDetails from './../../attendee/detail/components/label-details/labelDetails';
import TicketCard from './../../attendee/detail/components/ticket-card/ticketCard';
import { useTranslation, Trans } from 'react-i18next';
import ListItem from './../../attendee/detail/components/list-item/listItem';
import Loading from '../../../components/loading';
import { spec, initAction, ListDropdownItems } from '../list/list';

import Style from './detail.module.css';
import SwitchItem from '../../attendee/detail/components/switch-item/SwitchItem';
import OrderModel from '../models/orderModel';
import RefundModel from '../../../models/refundModel';
import OrderPaymentPlanDetail from '../../../models/orderPaymentPlanDetail';
import moment from 'moment-timezone';
import { capitalizeFirstLetter, translateLocale } from '../../attendee/detail/components/utils';
import TransactionDetails from '../../attendee/detail/components/transaction-details/transactionDetails';
import RefundDetails from '../../attendee/detail/components/refund/refundDetails';
import UserRightsRender from '../../events/dashboard/components/userRightsRender';
import Billing from './billing';
import ActionContainer from '../action-container/actionContainer';
import { AttendeeStatus } from '../../attendee/list/components/attendee-row/attendee-row';
import PaymentMethod from './components/payment-method/paymentMethod';
import FilledForm from '../../attendee/detail/components/filled-form/filledForm';
import PaymentUrl from './paymentUrl';
import SessionsList from './components/sessions-list/sessionsList';
import RefundStyle from '../../attendee/detail/components/refund/refundDetails.module.css';
import { useSegment, useTrackingPage } from '../../../components/tracking/trackingProvider';
import PaymentPlanDetail from './paymentPlanDetail';

function LabelHeader({ order }) {
    const { t } = useTranslation();

    return (
        <div className={Style['header-label-container']}>
            <Icon className={Style['header-icon-order']} name="package" />
            <div className={Style['header-label-order']}>
                <span className={Style['header-label']}>{` ${t('ticket.common.order')} : `}</span>
                <span>{order.resolved_buyer_name}</span>
            </div>
            <OrderStatus className={Style['header-status']} order={order} />
        </div>
    );
}

LabelHeader.propTypes = {
    order: PropTypes.object.isRequired
};

export function OrderDate({ date }) {
    const [{ event }] = useAppContext();

    const dateObject = new Date(date);
    //@Todo useless ?
    const tzDate = moment(dateObject).tz(event.timezone, true);

    const formatter_date = new Intl.DateTimeFormat(event.default_language, { dateStyle: 'short' });
    const formatted_date = formatter_date.format(tzDate);
    const formatter_time = new Intl.DateTimeFormat(event.default_language, { timeStyle: 'short' });
    const formatted_time = formatter_time.format(tzDate);

    return (
        <>
            <div className={Style['medium']}>{formatted_date}</div>
            <div style={{ marginTop: '5px' }}>
                {formatted_time} {event.timezone}
            </div>
        </>
    );
}

OrderDate.propTypes = {
    date: PropTypes.string.isRequired
};

export const OrderStatus = React.memo(({ order, className = '', list = false, items = [] }) => {
    const { t } = useTranslation();

    const status = React.useMemo(() => {
        if (!order) {
            return { color: 'red', status: 'error', text: 'error' };
        }

        if (order.attendee_count === order.attendees_deleted_count) {
            return { color: 'red', status: 'deleted', text: t('ticket.manager.status.deleted') };
        }

        if (!order.attendees.some(a => !a['refund_ticket_office']) && order.origin === 'weez_ticketoffice') {
            return { color: 'blue', status: t('ticket.manager.status.ticketOfficeRefund'), text: t('ticket.manager.status.ticketOfficeRefund') };
        }

        if (order.status === 'reservation') {
            return { color: 'orange', status: 'reservation', text: t('ticket.manager.status.reserved') };
        }

        if (order.status === 'valid') {
            return { color: 'green', status: 'valid', text: t('ticket.manager.status.paid') };
        }

        if (order.status === 'unknown') {
            return { color: 'blue', status: 'unknown', text: t('ticket.manager.status.notCompleted') };
        }
        return { color: 'red', status: 'error' };
    }, [order]);

    return (
        <>
            {list && ['valid', 'reservation'].includes(status.status) && !order.internal_transaction ? (
                <Status.Dropdown className={className} responsive text={status.text} color={status.color} items={items} />
            ) : (
                <Tooltip triggerOnHover position={'top-end'} title={t('ticket.manager.status.tooltipOnlinePayment')} margin={false}>
                    <Status className={className} responsive text={status.text} color={status.color} />
                </Tooltip>
            )}
        </>
    );
});

OrderStatus.propTypes = {
    order: PropTypes.object.isRequired,
    className: PropTypes.string
};

function TransactionCard({ order, event, locale, id }) {
    const { t } = useTranslation();

    let selectIcon = key => {
        switch (key) {
            case 1:
                return 'credit-card';
            case 2:
                return 'shirt';
            case 3:
                return 'service';

            default:
                break;
        }
    };

    if (order.cross_sellings && order.cross_sellings.length > 0) {
        const crossSellingsList = [];
        order.cross_sellings.forEach(item => {
            // Find the index for the variant in the cross sellings list
            const position = crossSellingsList.findIndex(e => e.variant.id === item.variant.id);

            if (position !== -1) {
                // Found a matching object, increment its count
                crossSellingsList[position].count += 1;
            } else {
                // did not find an object, add a new one
                crossSellingsList.push({ ...item, count: 1 });
            }
        });
        order.cross_sellings = crossSellingsList;
    }

    const discount_subtitle = React.useMemo(() => {
        const discount_data = [order.discount_code?.discount?.name, order.discount_code?.code];
        return discount_data.filter(x => x).join(' · ');
    }, [order]);

    const crossSellingsType = React.useMemo(() => {
        let cross_sellings_attendees = [];
        let cross_sellings_buyer = [];

        if (!order.cross_sellings) {
            return {
                cross_sellings_attendees: [],
                cross_sellings_buyer: []
            };
        }
        order.cross_sellings.forEach(cross_selling => {
            if (cross_selling.variant?.product?.for_buyer) {
                cross_sellings_buyer.push(cross_selling);
            } else {
                cross_sellings_attendees.push(cross_selling);
            }
        });
        return {
            cross_sellings_attendees,
            cross_sellings_buyer
        };
    }, [order]);
    return (
        <TransactionDetails id={id} title={t('ticket.manager.orders.detail.orderDetails')}>
            {order.attendees_by_rate.length > 0 &&
                order.attendees_by_rate.map(countByRate => (
                    <TransactionDetails.Item
                        key={countByRate.rate_id}
                        title={countByRate.group_name ? `${countByRate.rate_name} (${countByRate.group_name})` : countByRate.rate_name}
                        icon="tickets"
                        quantity={countByRate.attendees_count + countByRate.deleted_attendees_count}
                    >
                        <Numbers value={countByRate.price_ticket} locale={locale} currency={event?.currency || 'EUR'} showNullDecimal />
                    </TransactionDetails.Item>
                ))}
            {order.attendees_deleted_count > 0 && (
                <TransactionDetails.Item title={t('ticket.manager.orders.detail.countDeleted')}>
                    <Numbers value={-order.attendees_deleted_count} locale={locale} />
                </TransactionDetails.Item>
            )}
            {+order.total_fees_ttc > 0 && (
                <TransactionDetails.Item title={t('ticket.manager.totalFees')}>
                    <Numbers value={order.total_fees_ttc} locale={locale} currency={event?.currency || 'EUR'} showNullDecimal />
                </TransactionDetails.Item>
            )}
            {+order.total_reduction_amount > 0 && (
                <TransactionDetails.Item title={<InfoText subtitle={discount_subtitle}>{t('ticket.manager.orders.detail.totalReduction')}</InfoText>}>
                    <Numbers value={-order.total_reduction_amount} locale={locale} currency={event?.currency || 'EUR'} showNullDecimal />
                </TransactionDetails.Item>
            )}

            {crossSellingsType.cross_sellings_attendees
                .filter(e => e && e.variant && e.variant.product)
                .map(e => {
                    return (
                        <>
                            <TransactionDetails.ItemAction
                                quantity={e.count}
                                title={e.variant.product.name + ' - ' + e.variant.name}
                                icone={selectIcon(e.variant.product.category)}
                            >
                                <Numbers value={e.paid_price} locale={locale} currency={event?.currency || 'EUR'} showNullDecimal />
                            </TransactionDetails.ItemAction>
                        </>
                    );
                })}
            {crossSellingsType.cross_sellings_buyer
                .filter(e => e && e.variant && e.variant.product)
                .map(e => {
                    return (
                        <>
                            <TransactionDetails.ItemAction
                                quantity={e.count}
                                number={e.barcode}
                                title={e.variant.product.name + ' - ' + e.variant.name}
                                icone={selectIcon(e.variant.product.category)}
                            >
                                <Numbers value={e.paid_price} locale={locale} currency={event?.currency || 'EUR'} showNullDecimal />
                            </TransactionDetails.ItemAction>
                        </>
                    );
                })}
            <TransactionDetails.Item title={t('ticket.manager.totalPrice')} blue>
                <Numbers value={order.price_ticket} locale={locale} currency={event?.currency || 'EUR'} showNullDecimal />
            </TransactionDetails.Item>
            {order.attendee_count > order.attendees_deleted_count && ['valid', 'reservation'].includes(order.status) && (
                <>
                    <TransactionDetails.Item title={t('ticket.manager.orders.detail.amountPaid')}>
                        <Numbers value={order.status === 'valid' ? order.price_ticket : 0} locale={locale} currency={event?.currency || 'EUR'} showNullDecimal />
                    </TransactionDetails.Item>
                    <TransactionDetails.Item title={t('ticket.manager.orders.detail.amountToBePaid')} blue>
                        <Numbers value={order.status === 'reservation' ? order.price_ticket : 0} locale={locale} currency={event?.currency || 'EUR'} showNullDecimal />
                    </TransactionDetails.Item>
                </>
            )}
        </TransactionDetails>
    );
}

TransactionCard.propTypes = {
    order: PropTypes.object.isRequired,
    event: PropTypes.object.isRequired,
    locale: PropTypes.string.isRequired
};

function RefundContentWrapper(order) {
    function RefundContent({ refund, currencyFormatter, type = 'create' }) {
        const { t } = useTranslation();

        let key = '';
        if (refund.owner === 'attendee') {
            if (type === 'remove') {
                key = 'ticket.manager.orders.refund_detail_attendee_deleted';
            } else {
                key = 'ticket.manager.orders.refund_detail_attendee';
            }
        } else {
            if (type === 'remove') {
                key = 'ticket.manager.orders.refund_detail_order_deleted';
            } else {
                key = 'ticket.manager.orders.refund_detail_order';
            }
        }
        const attendee = order.attendees.find(attendee => attendee.id === refund.attendee);

        let origin_translations = React.useMemo(() => {
            if (refund.origin !== null) {
                return t('ticket.manager.attendee.refund_origin_widget', { origin: refund.origin });
            }
            if (refund.account) {
                return t('ticket.manager.attendee.refund_origin_bo', {
                    first_name: refund.account.first_name,
                    last_name: refund.account.last_name,
                    email: refund.account.email
                });
            }
            return '';
        }, [refund]);

        return (
            <Trans
                i18nKey={key}
                values={{
                    attendee,
                    amount: currencyFormatter.format(refund.amount),
                    fees_amount: refund.fees_amount,
                    bluetext_className: RefundStyle['blue-text'],
                    origin: origin_translations
                }}
                components={{
                    span: <span />
                }}
            />
        );
    }

    return RefundContent;
}

function RefundCard({ order, event, locale, onRefundDeleted, refunds = [] }) {
    const { t } = useTranslation();
    if (!order.refund_requests?.length && !refunds?.length) {
        return null;
    }
    return (
        <Card id="refunds">
            <HeaderCard>
                <Card.Title>{t('ticket.manager.orders.refunds_title')}</Card.Title>
            </HeaderCard>
            <div style={{ padding: '15px' }}>
                <RefundDetails
                    locale={locale}
                    currency={event.currency}
                    refundRequests={order?.refund_requests}
                    refunds={refunds}
                    timezone={event.timezone}
                    onRefundDeleted={onRefundDeleted}
                    refundContent={RefundContentWrapper(order)}
                />
            </div>
        </Card>
    );
}

export function OrderDetail() {
    const { t } = useTranslation();
    const { track } = useSegment();

    const { order_id } = useParams();
    const [{ event, organization }] = useAppContext();
    const history = useHistory();
    const locale = i18n.language;
    const ATTENDEE_LIST_MAX_LENGTH = 10;

    const [action, setAction] = React.useState(initAction);

    const prefix = React.useMemo(() => {
        return `/ticket/O${organization.id}/E${event.id}/manager`;
    }, [event, organization]);

    React.useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, []);

    const [order, loading, sync] = OrderModel.useApiModel(
        {
            id: order_id,
            eventId: event.pk,
            organizationId: organization.pk,
            query: {
                sync: true,
                include_deleted_fields: true,
                include_default_fields: true,
                expand: ['discount_code.discount', 'attendees.rate', 'invoice', 'refund_requests', 'cross_sellings', 'cross_sellings.variant.product'],
                include: ['attendees_by_rate,invoice', 'buyer_form.field', 'cross_sellings']
            }
        },
        [order_id]
    );

    const [refunds, loadingRefunds] = RefundModel.useApiModel(
        {
            orderId: order_id,
            eventId: event.pk,
            organizationId: organization.pk
        },
        [order_id, event, organization]
    );

    const [paymentPlan, loadingPaymentPlan] = OrderPaymentPlanDetail.useApiModel(
        {
            launch: Boolean(order?.is_payment_plan),
            organizationId: organization?.pk,
            eventId: event?.pk,
            id: order?.id_transaction
        },
        [organization, event, order]
    );

    const displayOriginName = React.useMemo(() => {
        if (order) {
            if (order.origin === 'weez_distributor') {
                return order.origin_detail;
            }
            if (order.origin === 'weez_ticketoffice') {
                return t('ticket.manager.orders.detail.ticket_office', { value: order.origin_detail });
            }
            return t('ticket.manager.orders.origin.' + order.origin);
        }
    }, [order, t]);

    useTrackingPage('VIEW - ORDER DETAILS', { 'Page Title': 'ORDER DETAILS' });

    const addNavTracking = (translation, element) => {
        return <div onClick={() => track('ACTION > ANCHOR NAV', { 'Page Title': 'ORDER DETAILS', 'Anchor element': element })}>{translation}</div>;
    };

    if (loading || (!loading && !order) || loadingRefunds) {
        return <Loading />;
    }
    return (
        <>
            <WeezHeader
                title={<LabelHeader order={order} />}
                backLink={{
                    onClick: () => {
                        history.push(`${prefix}/orders`);
                        track('ACTION > BACK', { 'Page Title': 'ORDER DETAILS' });
                    }
                }}
                rightComponent={[
                    <div className={Style['switch-order-container']} key="right-block">
                        <SwitchItem type="orders" itemId={order.pk} prefix={prefix} primaryKey="id_transaction" spec={spec} />
                        <ButtonDropdown
                            key="dropdown"
                            size="large"
                            right
                            renderWhenClosed
                            trigger={<Button inverted primary icon={<Icon name="chevron-bottom" />} label={t('ticket.common.cta.actions')} />}
                            items={[<ListDropdownItems key="list" order={order} paymentPlan={paymentPlan} onChangeAction={setAction} mode="detail" />]}
                        />
                    </div>
                ]}
            >
                <Link to={`${prefix}/orders`} onClick={() => track('ACTION > CLICK ORDER BREADCRUMB', { 'Page Title': 'ORDER DETAILS' })} className={Style['header-link-manager']}>
                    {t('ticket.manager.orders.orderManager')}
                </Link>
                {` > ${order.transaction_reference}`}
            </WeezHeader>
            <AnchorNav
                items={[
                    {
                        id: 'order',
                        title: addNavTracking(t('ticket.common.order'), 'Order')
                    },
                    {
                        id: 'details',
                        title: addNavTracking(t('ticket.manager.orders.detail.orderDetails'), 'Summary')
                    },
                    ...(order.refund_requests.length > 0
                        ? [
                              {
                                  id: 'refunds',
                                  title: addNavTracking(t('ticket.manager.orders.detail.refundRequests'), 'Refunds')
                              }
                          ]
                        : []),
                    {
                        id: 'buyer',
                        title: addNavTracking(t('ticket.manager.orders.detail.buyerInfos'), 'Buyer')
                    },
                    {
                        id: 'billing',
                        title: addNavTracking(t('ticket.common.billing'), 'Billing')
                    }
                ]}
                rootId="main"
            >
                <SplitContainer
                    additional={
                        <>
                            <Card className={Style['attendees-list__container']}>
                                <HeaderCard separator={false}>
                                    <div className={Style['attendees-list_title']}>
                                        {t('ticket.manager.orders.detail.ticketsSameOrder', { count: order.attendee_count })}
                                        <span className={Style['count-ticket']}>
                                            <Icon name="user" />x{order.attendees.length}
                                        </span>
                                    </div>
                                </HeaderCard>
                                {order.attendees.length > 0 &&
                                    order.attendees.map((attendee, index) => {
                                        if (index + 1 <= ATTENDEE_LIST_MAX_LENGTH) {
                                            return (
                                                <ListItem
                                                    key={index}
                                                    onClick={() => track('ACTION > ATTENDEE SWITCH', { 'Page Title': 'ORDER DETAILS' })}
                                                    deleted={attendee.removed || order.status === 'unknown'}
                                                    subtitle={attendee.id_weez_ticket}
                                                    url={`${prefix}/attendees/${attendee.id}`}
                                                    content={attendee.resolved_attendee_name}
                                                    status={<AttendeeStatus ledMode attendee={attendee} order={order} />}
                                                >
                                                    {attendee.resolved_attendee_name}
                                                </ListItem>
                                            );
                                        }
                                        return null;
                                    })}
                                {order.attendees.length > ATTENDEE_LIST_MAX_LENGTH && (
                                    <ListItem
                                        onClick={() => track('ACTION > ATTENDEE SWITCH', { 'Page Title': 'ORDER DETAILS' })}
                                        url={`${prefix}/attendees?search=${order.transaction_reference}`}
                                    >
                                        {t('ticket.manager.orders.detail.seeAllTickets')}
                                    </ListItem>
                                )}
                            </Card>
                            <FrameCard title={<span style={{ fontSize: '14px', fontWeight: '500' }}>{t('ticket.common.comments')}</span>}>
                                <Card.EmptyState icon={<Icon name="traffic-barrier" />} title={t('ticket.common.comments')} subtitle={t('ticket.common.constructBlock')} />
                            </FrameCard>
                        </>
                    }
                >
                    <ActionContainer sync={() => sync({ allow_cache: false, cache: false })} onClose={() => setAction(initAction)} {...action} />
                    <TicketCard id="order">
                        <TicketCard.Header>
                            <div>
                                <span style={{ fontSize: '16px', fontWeight: '500' }}>
                                    {t('ticket.manager.orders.orderIdLabel')}
                                    <CopyToClipboardMinimal hoverBackgroundColor={'white'}>{order.transaction_reference}</CopyToClipboardMinimal>
                                    {order.id_meta_order && (
                                        <span style={{ fontSize: '14px', fontWeight: 'normal' }}>
                                            {` ${t('ticket.manager.orders.orderMetaOrderLabel', { metaOrder: order.id_meta_order })}`}
                                        </span>
                                    )}
                                </span>
                            </div>
                            <LabelDetails.Container>
                                <LabelDetails title={t('ticket.common.orderDate')}>
                                    <OrderDate date={order.sold_date} />
                                </LabelDetails>
                                <LabelDetails title={t('ticket.manager.orders.ticketsNumber')}>
                                    <div className={Style['medium']}>{order.attendee_count}</div>
                                    {order.attendees_deleted_count > 0 && <div>{t('ticket.manager.orders.detail.includingDeleted', { count: order.attendees_deleted_count })}</div>}
                                </LabelDetails>
                                <LabelDetails title={t('ticket.common.origin')}>
                                    <div className={Style['medium']}>{displayOriginName}</div>
                                </LabelDetails>
                            </LabelDetails.Container>
                        </TicketCard.Header>
                        <TicketCard.Content className={Style['order-card__primary-infos']}>
                            <LabelDetails.Container>
                                <LabelDetails title={t('ticket.common.price')}>
                                    <div className={Style['order-card__price']}>
                                        <Numbers value={order.price_ticket} locale={locale} currency={event?.currency || 'EUR'} showNullDecimal />
                                    </div>
                                </LabelDetails>
                                <LabelDetails title={t('ticket.common.status')}>
                                    <OrderStatus order={order} />
                                </LabelDetails>
                                <PaymentMethod order={order} params={{ organizationId: organization.id, eventId: event.id }} />
                            </LabelDetails.Container>
                        </TicketCard.Content>
                        <TicketCard.Content>
                            <div className={Style['medium']}>{t('ticket.common.furtherInformation')}</div>
                            <LabelDetails.Container>
                                {order.discount && (
                                    <LabelDetails title={t('ticket.common.promocode')}>
                                        <div className={Style['medium']}>{order.discount.name}</div>
                                    </LabelDetails>
                                )}
                                <LabelDetails title={t('ticket.common.language')}>
                                    <div className={Style['medium']}>{capitalizeFirstLetter(translateLocale(locale, order.locale))}</div>
                                </LabelDetails>
                            </LabelDetails.Container>
                            <UserRightsRender>
                                {getMenhirUrl() && (
                                    <a href={`${getMenhirUrl()}/bo/admin/command/follow?transaction=${order.transaction_reference}`} className={Style['link-sbo']}>
                                        <Icon className={Style['link-sbo__icon']} name="package" />
                                        {t('ticket.manager.orders.detail.followOrder')}
                                    </a>
                                )}
                            </UserRightsRender>
                        </TicketCard.Content>
                        <PaymentUrl order={order} setAction={setAction} />
                    </TicketCard>
                    <SessionsList order={order} />
                    <TransactionCard id="details" order={order} event={event} locale={locale} />
                    <RefundCard order={order} event={event} locale={locale} refunds={refunds} />
                    <FilledForm
                        id="buyer"
                        title={t('ticket.manager.orders.detail.buyerInfos')}
                        subtitle={t('ticket.manager.orders.detail.subtitle_buyerInfos')}
                        buyerForm={order.buyer_form}
                        order={order}
                        event={event}
                        usage={'buyer'}
                        locale={locale}
                        onSubmit={sync}
                        onEdit={() => track('ACTION > EDIT BUYER INFORMATION', { 'Page Title': 'ORDER DETAILS' })}
                    />
                    <Billing id="billing" order={order} onSubmit={sync} />
                    {order?.is_payment_plan && <PaymentPlanDetail id="payment_plan" paymentPlan={paymentPlan} loading={loading || loadingPaymentPlan} />}
                </SplitContainer>
            </AnchorNav>
        </>
    );
}
